import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface ReportAccountBalance {
  accountId: string;
  code: string;
  name: string;
  type: string;
  normalBalance: string;
  debit: number;
  credit: number;
  netBalance: number;
}

export function useTrialBalance(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'trial-balance', { startDate, endDate }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get<ReportAccountBalance[]>(`/api/accounts/reports/trial-balance?${params.toString()}`);
      return data;
    },
  });
}

export function useProfitAndLoss(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'profit-and-loss', { startDate, endDate }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get<{
        income: ReportAccountBalance[];
        expenses: ReportAccountBalance[];
        totalIncome: number;
        totalExpense: number;
        netProfit: number;
      }>(`/api/accounts/reports/profit-and-loss?${params.toString()}`);
      return data;
    },
  });
}

export function useBalanceSheet(asOfDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'balance-sheet', { asOfDate }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (asOfDate) params.append('asOfDate', asOfDate);
      const { data } = await api.get<{
        assets: ReportAccountBalance[];
        liabilities: ReportAccountBalance[];
        equity: ReportAccountBalance[];
        totalAssets: number;
        totalLiabilities: number;
        totalEquity: number;
        netProfit: number;
        totalLiabilitiesAndEquity: number;
      }>(`/api/accounts/reports/balance-sheet?${params.toString()}`);
      return data;
    },
  });
}

export interface CashFlowLine {
  code: string;
  name: string;
  amount: number;
}

export interface CashFlowStatement {
  period: { startDate: string; endDate: string };
  operating: { netProfit: number; adjustments: CashFlowLine[]; total: number };
  investing: { items: CashFlowLine[]; total: number };
  financing: { items: CashFlowLine[]; total: number };
  netChangeInCash: number;
  openingCash: number;
  closingCash: number;
  closingCashByAccount: CashFlowLine[];
  /** Zero when the statement ties to the cash ledger; reported honestly if not. */
  unreconciledDifference: number;
}

/** Cash Flow Statement (indirect method) — the third core financial statement. */
export function useCashFlow(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'cash-flow', { startDate, endDate }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get<CashFlowStatement>(`/api/accounts/reports/cash-flow?${params.toString()}`);
      return data;
    },
    enabled: !!startDate && !!endDate,
  });
}

export function useTaxSummary(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'tax-summary', { startDate, endDate }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get<{
        taxAccounts: ReportAccountBalance[];
        totalNetBalance: number;
      }>(`/api/accounts/reports/tax-summary?${params.toString()}`);
      return data;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Advanced report readers — the surfaces in src/api-accounts/reports-advanced.js
// and src/api-accounts/bank-cash.js that had no screen. Every path below was
// verified against the router before wiring:
//   GET /api/accounts/reports/bankbook?startDate=&endDate=
//   GET /api/accounts/cash/petty?date=YYYY-MM-DD
//   GET /api/accounts/reports/daily-collection?date=YYYY-MM-DD
//   GET /api/accounts/reports/daily-payment?date=YYYY-MM-DD
//   GET /api/accounts/reports/monthly-summary?month=YYYY-MM
//   GET /api/accounts/reports/customer-aging
//   GET /api/accounts/reports/vendor-aging
// All seven sit behind requireAccountsUser, which is requireAccountsAccess: the
// accounts department or an admin, nothing finer. Everyone else is answered
// 403 { error: 'Access denied. Accounts department only.' }, and the screens
// below render that instead of an empty table.
// ─────────────────────────────────────────────────────────────────────────────

/** One posted ledger line with the running balance the server accumulated. */
export interface ReportLedgerLine {
  date: string;
  narration?: string;
  voucherType?: string;
  debit: number;
  credit: number;
  balance: number;
}

/** Bank book (COA 1020) / cash book (COA 1010) — same shape from both routes. */
export interface DayBookReport {
  openingBalance: number;
  closingBalance: number;
  entries: ReportLedgerLine[];
}

/** GET /api/accounts/reports/bankbook — the bank ledger account (1020). */
export function useBankBook(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'bankbook', { startDate, endDate }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get<DayBookReport>(`/api/accounts/reports/bankbook?${params.toString()}`);
      return data;
    },
  });
}

/** A cash/petty line is timed, not dated, so it carries its own shape. */
export interface CashBookLine {
  id: string;
  time?: string;
  narration?: string;
  voucherType?: string;
  cashIn: number;
  cashOut: number;
  balance: number;
}

export interface CashBookReport {
  date: string;
  account?: { code: string; name: string };
  openingBalance: number;
  closingBalance: number;
  totalCashIn: number;
  totalCashOut: number;
  entries: CashBookLine[];
}

/**
 * GET /api/accounts/cash/petty — petty cash (COA 1030) for one day.
 * 404s when the chart of accounts has no 1030; the caller shows that verbatim.
 */
export function usePettyCash(date: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'cash', 'petty', date],
    queryFn: async () => (await api.get<CashBookReport>(`/api/accounts/cash/petty?date=${encodeURIComponent(date)}`)).data,
    enabled: !!date,
  });
}

/** One posted receipt or payment on a daily collection / payment report. */
export interface DailyMoneyLine {
  paymentNumber?: string;
  /** `customer` on the collection report, `vendor` on the payment report. */
  customer?: string;
  vendor?: string;
  amount: number;
  method?: string;
  reference?: string;
}

export interface DailyMoneyReport {
  date: string;
  total: number;
  count: number;
  receipts?: DailyMoneyLine[];
  payments?: DailyMoneyLine[];
}

/** GET /api/accounts/reports/daily-collection — posted receipts for one day. */
export function useDailyCollection(date: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'daily-collection', date],
    queryFn: async () => (await api.get<DailyMoneyReport>(`/api/accounts/reports/daily-collection?date=${encodeURIComponent(date)}`)).data,
    enabled: !!date,
  });
}

/** GET /api/accounts/reports/daily-payment — posted payments for one day. */
export function useDailyPayment(date: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'daily-payment', date],
    queryFn: async () => (await api.get<DailyMoneyReport>(`/api/accounts/reports/daily-payment?date=${encodeURIComponent(date)}`)).data,
    enabled: !!date,
  });
}

export interface MonthlyExpenseLine {
  code?: string;
  name?: string;
  amount: number;
}

export interface MonthlySummaryReport {
  month: string;
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  expenseBreakdown: MonthlyExpenseLine[];
  invoicesIssued: number;
  billsReceived: number;
  totalCollections: number;
  collectionsCount: number;
  totalPaymentsMade: number;
  paymentsCount: number;
}

/** GET /api/accounts/reports/monthly-summary?month=YYYY-MM */
export function useMonthlySummary(month: string) {
  return useQuery({
    queryKey: ['accounts', 'reports', 'monthly-summary', month],
    queryFn: async () => (await api.get<MonthlySummaryReport>(`/api/accounts/reports/monthly-summary?month=${encodeURIComponent(month)}`)).data,
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
}

/**
 * One party's outstanding balance split by how overdue it is. The buckets come
 * from the server, computed against unpaid / partially-paid / overdue documents
 * only, so a zero bucket really means nothing is sitting in that age range.
 */
export interface AgingRow {
  id: string;
  name: string;
  current: number;
  days0_30: number;
  days30_60: number;
  days60_90: number;
  days90Plus: number;
  total: number;
}

/** GET /api/accounts/reports/customer-aging — receivables by client. */
export function useCustomerAging() {
  return useQuery({
    queryKey: ['accounts', 'reports', 'customer-aging'],
    queryFn: async () => (await api.get<AgingRow[]>('/api/accounts/reports/customer-aging')).data,
  });
}

/** GET /api/accounts/reports/vendor-aging — payables by supplier. */
export function useVendorAging() {
  return useQuery({
    queryKey: ['accounts', 'reports', 'vendor-aging'],
    queryFn: async () => (await api.get<AgingRow[]>('/api/accounts/reports/vendor-aging')).data,
  });
}
